import React from "react";
import PropTypes from "prop-types";
import Select, { components } from "react-select";
import { getCountries, getCountryCallingCode } from "react-phone-number-input";
import en from "react-phone-number-input/locale/en";

// Build default country options with flags and dialing codes
export const defaultCountryOptions = [
  {
    value: "ZZ",
    label: en.ZZ,
    dial: "",
    flag: () => (
      <span role="img" aria-label="International" style={{ marginRight: 8 }}>
        🌐
      </span>
    ),
  },
  ...getCountries().map((code) => ({
    value: code,
    label: en[code],
    dial: `+${getCountryCallingCode(code)}`,
    flag: () => (
      <img
        src={`https://purecatamphetamine.github.io/country-flag-icons/3x2/${code}.svg`}
        alt={`${en[code]} flag`}
        style={{ width: 16, height: 11, marginRight: 8 }}
      />
    ),
  })),
];

// Custom option renderer for react-select
const Option = (props) => (
  <components.Option {...props}>
    <div className="tw-flex tw-items-center">
      {props.data.flag()}
      <span>
        {props.data.label}
        {props.data.dial ? ` (${props.data.dial})` : ""}
      </span>
    </div>
  </components.Option>
);

// Custom single-value renderer for react-select
const SingleValue = (props) => (
  <components.SingleValue {...props}>
    <div className="tw-flex tw-items-center">
      {props.data.flag()}
      <span>{props.data.dial}</span>
    </div>
  </components.SingleValue>
);

// Default react-select styles for the country selector
const defaultSelectStyles = {
  control: (provided) => ({
    ...provided,
    minHeight: "2.5rem",
    borderRadius: provided.borderRadius, // will be overridden if prop provided
    borderTopRightRadius: 0,
    borderBottomRightRadius: 0,
  }),
  menu: (provided) => ({
    ...provided,
    marginTop: 0,
    width: "auto",
    minWidth: 250,
  }),
  dropdownIndicator: (provided) => ({
    ...provided,
    padding: "0 8px",
    width: 30,
  }),
  indicatorSeparator: () => ({ display: "none" }),
};

/**
 * PhoneNumberInput
 *
 * Combines a country selector (flags + dialing codes) and a numeric-only telephone input.
 * Accepts a `borderRadius` prop to set equal rounding on both select and input.
 */
const PhoneNumber = ({
  selectedCountry,
  onSelectCountry,
  number,
  onChangeNumber,
  options = defaultCountryOptions,
  placeholder = "123-456-7890",
  // inline style overrides for the numeric input
  inputProps = {},
  // react-select style overrides
  selectStyles = {},
  // allow full rounding by passing a CSS value, e.g. "50%" or "9999px"
  borderRadius,
  isSearchable = true,
  disabled = false,
  required = false,
}) => {
  // Merge default and user-provided select styles, injecting borderRadius
  const mergedSelectStyles = {
    ...defaultSelectStyles,
    ...selectStyles,
    control: (provided, state) => {
      // get base from user override or default
      const base = selectStyles.control
        ? selectStyles.control(provided, state)
        : defaultSelectStyles.control(provided, state);
      return {
        ...base,
        borderRadius: borderRadius || base.borderRadius,
        borderTopRightRadius: 0,
        borderBottomRightRadius: 0,
      };
    },
    menu: selectStyles.menu || defaultSelectStyles.menu,
    dropdownIndicator:
      selectStyles.dropdownIndicator || defaultSelectStyles.dropdownIndicator,
    indicatorSeparator:
      selectStyles.indicatorSeparator || defaultSelectStyles.indicatorSeparator,
  };

  // Inline style for input borderRadius on left side removed
  const inputStyle = {
    borderRadius: `0 ${borderRadius} ${borderRadius} 0` || undefined,
    height: 40,
    ...(inputProps.style || {}),
  };

  return (
    <div
      className={`tw-flex tw-items-center ${disabled ? "tw-opacity-50" : ""}`}
    >
      {/* Country selector */}
      <div className="tw-w-32">
        <Select
          options={options}
          value={selectedCountry}
          onChange={onSelectCountry}
          components={{ Option, SingleValue }}
          isSearchable={isSearchable}
          isDisabled={disabled}
          styles={mergedSelectStyles}
          className="tw-rounded-start"
        />
      </div>

      {/* Phone number input */}
      <input
        type="tel"
        placeholder={placeholder}
        value={number}
        onChange={(e) => onChangeNumber(e.target.value.replace(/\D/g, ""))}
        disabled={disabled}
        required={required}
        {...inputProps}
        style={inputStyle}
      />
    </div>
  );
};

PhoneNumber.propTypes = {
  selectedCountry: PropTypes.shape({
    value: PropTypes.string.isRequired,
    label: PropTypes.string,
    dial: PropTypes.string,
    flag: PropTypes.func,
  }).isRequired,
  onSelectCountry: PropTypes.func.isRequired,
  number: PropTypes.string.isRequired,
  onChangeNumber: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string,
      dial: PropTypes.string,
      flag: PropTypes.func,
    })
  ),
  placeholder: PropTypes.string,
  inputProps: PropTypes.object,
  selectStyles: PropTypes.object,
  borderRadius: PropTypes.string,
  isSearchable: PropTypes.bool,
  disabled: PropTypes.bool,
  required: PropTypes.bool,
};

PhoneNumber.defaultProps = {
  options: defaultCountryOptions,
  placeholder: "123-456-7890",
  inputProps: {},
  selectStyles: {},
  borderRadius: undefined,
  isSearchable: true,
  disabled: false,
  required: false,
};

export default PhoneNumber;
